import * as React from 'react'
import { Slot } from 'radix-ui'

import { cn } from '@/lib/utils'
import { AlertIcon, SparkleIcon } from './vonk-icons'

/**
 * The tones of a status (spec-64-u2, U-106 / DS-2, DS-9), on U1's AA tokens:
 * `neutral` for facts (source, kind), `brand` for a light brand accent (deck, name),
 * `info`, `success`, `warning` (only when something must happen; always with an icon,
 * so it never relies on a hue next to the brand orange) and `danger`.
 */
export type StatusTone = 'neutral' | 'brand' | 'info' | 'success' | 'warning' | 'danger'

export interface StatusBadgeProps extends React.ComponentProps<'span'> {
  tone?: StatusTone
  /** `sm` 24 px (cards, lists), `md` 26 px (dossier head). */
  size?: 'sm' | 'md'
  /**
   * Leading icon, decoration (`aria-hidden`). Without it `warning` shows the alert glyph,
   * `brand` the sparkle and the other tones a 7 px dot.
   */
  icon?: React.ReactNode
  /** Danger without the breathing glow (a settled fact, e.g. a rejected deal). */
  calm?: boolean
  /** Render the child (a `Link`, a `button`) with the badge look. */
  asChild?: boolean
}

/**
 * The one status label: always a pill, 12 px semibold, one height per size. The label is
 * always text; colour and icon only support it (IA-20). Styled by `.vonk-badge` in
 * `index.css` (`@layer components`), so a caller's utilities still win.
 */
function StatusBadge({
  tone = 'neutral',
  size = 'sm',
  icon,
  calm = false,
  asChild = false,
  className,
  children,
  ...props
}: StatusBadgeProps) {
  const Comp = asChild ? Slot.Root : 'span'
  const glyph = icon ?? (tone === 'warning' ? <AlertIcon /> : tone === 'brand' ? <SparkleIcon /> : null)
  return (
    <Comp
      data-slot="status-badge"
      data-tone={tone}
      data-size={size}
      data-calm={calm ? '' : undefined}
      className={cn('vonk-badge', className)}
      {...props}
    >
      {glyph ? (
        <span aria-hidden data-slot="status-badge-icon" className="vonk-badge__icon vonk-duotone">
          {glyph}
        </span>
      ) : (
        <span aria-hidden data-slot="status-badge-dot" className="vonk-badge__dot" />
      )}
      {/* Slottable: with asChild the child takes the badge, and the icon goes inside it. */}
      <Slot.Slottable>{children}</Slot.Slottable>
    </Comp>
  )
}

export { StatusBadge }
